import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, RefreshCw, X } from 'lucide-react';
import { formatEngagementWhen } from '../../lib/emailEngagement';
import {
  loadPostmarkOutboundMessageDetails,
  loadPostmarkOutboundMessages,
  type PostmarkMessageEvent,
  type PostmarkOutboundMessage,
  type PostmarkOutboundMessageDetail,
} from '../../lib/postmarkOutboundMessages';
import type { ScheduledEmailRow } from '../Subscribers/types';

type RecipientSubscriber = {
  email: string | null;
  name: string | null;
  email_status: string | null;
} | null;

export type ScheduledEmailStatusModalRow = ScheduledEmailRow & {
  postmark_message_id?: string | null;
  subscribers: RecipientSubscriber;
};

interface ScheduledEmailStatusModalProps {
  isOpen: boolean;
  row: ScheduledEmailStatusModalRow | null;
  onClose: () => void;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function statusClasses(status: string | null | undefined): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'sent' || s === 'delivered') {
    return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800';
  }
  if (s === 'queued' || s === 'processing' || s === 'pending' || s === 'sending') {
    return 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-200 dark:border-amber-800';
  }
  if (s.includes('fail') || s.includes('bounce') || s.includes('spam') || s === 'canceled') {
    return 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200 border-red-200 dark:border-red-800';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';
}

function eventTone(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('bounce') || t.includes('spam') || t.includes('fail')) {
    return 'border-rose-200 dark:border-rose-800 bg-rose-50/70 dark:bg-rose-900/20';
  }
  if (t.includes('deliver') || t === 'sent') {
    return 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-900/20';
  }
  if (t.includes('open') || t.includes('click')) {
    return 'border-sky-200 dark:border-sky-800 bg-sky-50/70 dark:bg-sky-900/20';
  }
  return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40';
}

function EventRow({ event }: { event: PostmarkMessageEvent }) {
  const detail =
    event.bounceDescription ||
    event.summary ||
    (event.bounceType ? `Bounce type: ${event.bounceType}` : null);
  return (
    <li className={`rounded-lg border px-3 py-2.5 ${eventTone(event.type)}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-gray-900 dark:text-white">{event.type}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          {formatWhen(event.receivedAt)}
        </span>
      </div>
      {detail ? (
        <p className="mt-1 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{detail}</p>
      ) : null}
      {event.link ? (
        <a
          href={event.link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 block text-xs text-emerald-600 dark:text-emerald-400 hover:underline truncate"
          title={event.link}
        >
          {event.link}
        </a>
      ) : null}
    </li>
  );
}

export default function ScheduledEmailStatusModal({
  isOpen,
  row,
  onClose,
}: ScheduledEmailStatusModalProps) {
  const email = row?.subscribers?.email?.trim() ?? '';
  const messageId =
    (typeof row?.postmark_message_id === 'string' && row.postmark_message_id.trim()) || null;

  const [detail, setDetail] = useState<PostmarkOutboundMessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [history, setHistory] = useState<PostmarkOutboundMessage[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !row) return;

    let cancelled = false;

    const loadDetail = async () => {
      if (!messageId) {
        setDetail(null);
        setDetailError(null);
        setDetailLoading(false);
        return;
      }
      setDetailLoading(true);
      setDetailError(null);
      try {
        const next = await loadPostmarkOutboundMessageDetails(messageId, email || null);
        if (!cancelled) setDetail(next);
      } catch (e) {
        if (!cancelled) {
          setDetail(null);
          setDetailError(e instanceof Error ? e.message : 'Could not load Postmark message');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    const loadHistory = async () => {
      if (!email) {
        setHistory([]);
        setHistoryTotal(0);
        setHistoryError(null);
        setHistoryLoading(false);
        return;
      }
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const result = await loadPostmarkOutboundMessages(email, { count: 25, offset: 0 });
        if (!cancelled) {
          setHistory(result.messages);
          setHistoryTotal(result.totalCount);
        }
      } catch (e) {
        if (!cancelled) {
          setHistory([]);
          setHistoryTotal(0);
          setHistoryError(e instanceof Error ? e.message : 'Could not load address history');
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    void loadDetail();
    void loadHistory();

    return () => {
      cancelled = true;
    };
  }, [isOpen, row?.id, messageId, email]);

  if (!isOpen || !row) return null;

  const refresh = () => {
    // Re-trigger effect by toggling via remount keys — call loaders inline.
    const run = async () => {
      if (messageId) {
        setDetailLoading(true);
        setDetailError(null);
        try {
          setDetail(await loadPostmarkOutboundMessageDetails(messageId, email || null));
        } catch (e) {
          setDetail(null);
          setDetailError(e instanceof Error ? e.message : 'Could not load Postmark message');
        } finally {
          setDetailLoading(false);
        }
      }
      if (email) {
        setHistoryLoading(true);
        setHistoryError(null);
        try {
          const result = await loadPostmarkOutboundMessages(email, { count: 25, offset: 0 });
          setHistory(result.messages);
          setHistoryTotal(result.totalCount);
        } catch (e) {
          setHistory([]);
          setHistoryTotal(0);
          setHistoryError(e instanceof Error ? e.message : 'Could not load address history');
        } finally {
          setHistoryLoading(false);
        }
      }
    };
    void run();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-3xl flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="scheduled-email-status-title"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <h2
              id="scheduled-email-status-title"
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              Send report
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {email || 'No email on file'}
              {row.subscribers?.name ? ` · ${row.subscribers.name}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={refresh}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md"
              aria-label="Refresh"
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 ${detailLoading || historyLoading ? 'animate-spin' : ''}`}
              />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-5 py-4 space-y-6 overflow-y-auto flex-1 min-h-0">
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              This scheduled send
            </h3>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/80">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-4 py-3 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Engine status
                  </div>
                  <span
                    className={`mt-1 inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${statusClasses(row.status)}`}
                  >
                    {row.status}
                  </span>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Subscriber email status
                  </div>
                  <div className="mt-1 text-gray-900 dark:text-white">
                    {row.subscribers?.email_status ?? '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Scheduled
                  </div>
                  <div className="mt-1 text-gray-900 dark:text-white">
                    {formatWhen(row.scheduled_at)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Sent
                  </div>
                  <div className="mt-1 text-gray-900 dark:text-white">{formatWhen(row.sent_at)}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Subject
                  </div>
                  <div className="mt-1 text-gray-900 dark:text-white">
                    {row.subject?.trim() || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Attempts
                  </div>
                  <div className="mt-1 text-gray-900 dark:text-white">{row.attempts}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Postmark Message ID
                  </div>
                  <div className="mt-1 font-mono text-xs text-gray-800 dark:text-gray-200 break-all">
                    {messageId ?? '—'}
                  </div>
                </div>
                {row.error ? (
                  <div className="sm:col-span-2">
                    <div className="text-[11px] uppercase tracking-wide text-rose-600 dark:text-rose-400">
                      Engine error
                    </div>
                    <p className="mt-1 text-sm text-rose-700 dark:text-rose-300 whitespace-pre-wrap">
                      {row.error}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
            {row.subscriber_id ? (
              <Link
                to={`/subscribers/${row.subscriber_id}?tab=postmark`}
                className="inline-flex text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
                onClick={onClose}
              >
                Open connection Postmark tab
              </Link>
            ) : null}
          </section>

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Postmark message report
            </h3>
            {!messageId ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No Postmark Message ID on this row yet — common for pending, canceled, or failed
                before handoff. Address history below still applies when available.
              </p>
            ) : detailLoading && !detail ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
              </div>
            ) : detailError ? (
              <p className="text-sm text-rose-700 dark:text-rose-300">{detailError}</p>
            ) : detail ? (
              <div className="space-y-3">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-sm grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Postmark status
                    </div>
                    <span
                      className={`mt-1 inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${statusClasses(detail.status)}`}
                    >
                      {detail.status ?? '—'}
                    </span>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Received by Postmark
                    </div>
                    <div className="mt-1 text-gray-900 dark:text-white">
                      {formatWhen(detail.receivedAt)}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Subject
                    </div>
                    <div className="mt-1 text-gray-900 dark:text-white">
                      {detail.subject ?? '—'}
                    </div>
                  </div>
                  {detail.trackingEnabled ? (
                    <div className="sm:col-span-2 text-xs text-gray-600 dark:text-gray-400">
                      {detail.opened && detail.openedAt
                        ? `Opened ${formatEngagementWhen(detail.openedAt)}`
                        : 'No open recorded'}
                      {' · '}
                      {detail.clickCount > 0 && detail.lastClickAt
                        ? `${detail.clickCount} click${detail.clickCount === 1 ? '' : 's'} · ${formatEngagementWhen(detail.lastClickAt)}`
                        : 'No clicks'}
                    </div>
                  ) : (
                    <div className="sm:col-span-2 text-xs text-gray-500 dark:text-gray-400">
                      Open/click tracking was off for this send.
                    </div>
                  )}
                </div>
                {detail.events.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No delivery events returned for this message yet.
                  </p>
                ) : (
                  <ol className="space-y-2 list-none m-0 p-0">
                    {detail.events.map((ev, i) => (
                      <EventRow key={`${ev.type}-${ev.receivedAt}-${i}`} event={ev} />
                    ))}
                  </ol>
                )}
              </div>
            ) : null}
          </section>

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Address history on Postmark
            </h3>
            {!email ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No recipient email to look up.</p>
            ) : historyLoading && history.length === 0 ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
              </div>
            ) : historyError ? (
              <p className="text-sm text-rose-700 dark:text-rose-300">{historyError}</p>
            ) : history.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                <Mail className="w-6 h-6 mx-auto mb-2 opacity-40" />
                No outbound messages found for this address.
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Showing {history.length} of {historyTotal} newest messages to {email}.
                </p>
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Sent</th>
                        <th className="text-left px-3 py-2 font-medium">Subject</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                        <th className="text-left px-3 py-2 font-medium">This send</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                      {history.map((m) => {
                        const isCurrent = messageId != null && m.messageId === messageId;
                        return (
                          <tr
                            key={m.messageId}
                            className={
                              isCurrent
                                ? 'bg-emerald-50/60 dark:bg-emerald-900/15'
                                : 'hover:bg-gray-50/80 dark:hover:bg-gray-900/30'
                            }
                          >
                            <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
                              {formatWhen(m.receivedAt)}
                            </td>
                            <td className="px-3 py-2 min-w-[10rem]">
                              <span className="line-clamp-2 text-gray-900 dark:text-white">
                                {m.subject || '(no subject)'}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${statusClasses(m.status)}`}
                              >
                                {m.status ?? '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                              {isCurrent ? 'Current' : ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
